import { useTranslation } from 'react-i18next';
import { Order, OrderLineItem, ProductInterface } from '../../services/exports/Interfaces';
import CustomCarousel, { Item } from './CustomCarousel';
import useScreenType from '../../hooks/utility/useScreenType';
import MultiOrderProduct from '../menu/product/MultiOrderProduct';
import classnames from 'classnames';
import { ORDER_PRODUCT_HEIGHT } from '../../services/exports/Constants';
import useAlert from '../../hooks/utility/useAlert';
import useMenu from '../../hooks/menu/useMenu';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useMemo } from 'react';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { Modals } from '../../modules/mobile/exports/Enums';
import { toast } from 'react-hot-toast';
import { swipeableCssClass } from '../../modules/mobile/exports/Constants';

interface Props {
  order: Order;
  className?: string;
  itemClassName?: string;
  onProductClick?: (product: ProductInterface | null) => void;
}

const OrderCarousel = (props: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:OrderCarousel' });

  const { order, className, itemClassName, onProductClick } = props;

  const { toggleMenuItemModal } = useAppMethods();
  const { compileProduct } = useMenu();
  const { isDesktop } = useScreenType();
  const alert = useAlert();
  const { openModal, closeModal } = useModalRouter();
  const { isMobileApp } = useMobileApp();

  const onClick = (item: OrderLineItem) => {
    const product = compileProduct(item.product_id);

    if (!product) {
      return alert.error({
        description: t('error_messages.product_does_not_exist'),
      });
    }

    if (onProductClick) {
      return onProductClick(product);
    }

    if (isMobileApp) {
      return openModal({
        id: Modals.MenuItem,
        presentation: {
          initialBreakpoint: 1,
          breakpoints: [0, 1],
          handle: false,
          cssClass: swipeableCssClass,
        },
        props: {
          product: { id: item.product_id },
          lineItem: {
            product_id: item.product_id,
            quantity: 1,
            note: item.note,
            modifiers: item.modifiers,
          },
          onCloseModal: () => {
            closeModal();
            toast.success(t('success_messages.product_added_to_card'));
          },
        },
      });
    }

    toggleMenuItemModal({
      openModal: true,
      product: { id: item.product_id },
      lineItem: {
        product_id: item.product_id,
        quantity: 1,
        note: item.note,
        modifiers: item.modifiers,
      },
    });
  };

  const items = useMemo<Item[]>(
    () =>
      order.line_items
        .filter((item) => item.is_available)
        .map((item) => ({
          data: item,
          render: () => (
            <MultiOrderProduct product={item} className={itemClassName} toggleProduct={() => onClick(item)} />
          ),
        })),
    [order?.line_items, onClick, itemClassName],
  );

  return (
    items.length > 0 && (
      <div className={classnames('relative w-full', className)}>
        <CustomCarousel
          items={items}
          itemsPerSlide={isDesktop ? 3.5 : 1.25}
          slideJump={isDesktop ? 3 : 2}
          itemHeight={ORDER_PRODUCT_HEIGHT - 6}
          arrowsPosition="top"
          arrowsClassName="lg:mr-none mr-md"
          itemsClassName="lg:pl-none pl-md"
          title={<h5 className="lg:ml-none ml-md font-semibold">{t('order_again')}</h5>}
          headerClassName="mb-xs"
        />
      </div>
    )
  );
};

export default OrderCarousel;
